import { useEffect, useState, useCallback } from 'react';
import { Box, Typography, IconButton, Stack } from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';

import { useApi } from '@/providers/ApiContext';
import { getPlotMarkers, deletePlotMarker } from '@/actions/plotMarkers';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';
import PlotMarker from './PlotMarker';
import PLOT_STATUSES from '@/components/plotStatuses';
import type {
  PlotMarker as PlotMarkerType,
  EntityId,
} from '@/types';

interface PlotMarkerListProps {
  plotId: string | undefined;
  waypointId: EntityId;
  plotStatus?: string;
  onAddMarker: () => void;
  refreshTrigger: number;
  onMarkersChange: () => void | Promise<void>;
}

function PlotMarkerList({
  plotId,
  waypointId,
  plotStatus,
  onAddMarker,
  refreshTrigger,
  onMarkersChange,
}: PlotMarkerListProps): React.JSX.Element {
  const api = useApi();
  const [markers, setMarkers] = useState<PlotMarkerType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [api_error, setApiError] = useState<string | null>(null);

  const fetchMarkers = useCallback(async () => {
    if (!plotId || !waypointId || !api) return;

    try {
      setLoading(true);
      setApiError(null);
      const data = await getPlotMarkers(api, plotId as EntityId, waypointId);
      setMarkers(data.results || []);
    } catch (err) {
      console.error('Error fetching markers:', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Unable to load plot markers. This could be due to network issues, server problems, or the waypoint may not exist.';
      setApiError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [api, plotId, waypointId]);

  useEffect(() => {
    fetchMarkers();
  }, [fetchMarkers]);

  // Refresh when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger) {
      fetchMarkers();
    }
  }, [refreshTrigger, fetchMarkers]);

  const handleDeleteMarker = async (markerId: EntityId) => {
    if (!api) return;
    try {
      if (!plotId) return;
      await deletePlotMarker(api, plotId as EntityId, waypointId, markerId);
      // Refresh the markers list
      fetchMarkers();
      // Notify parent component that markers have changed
      if (onMarkersChange) {
        onMarkersChange();
      }
    } catch (error) {
      console.error('Error deleting marker:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Unable to delete the plot marker. This could be due to network issues, server problems, or insufficient permissions.';
      setApiError(errorMessage);
    }
  };

  return (
    <Box>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{ mb: 1 }}
      >
        <Typography variant='body2' color='text.secondary'>
          {markers.length === 0
            ? 'No documents uploaded'
            : `${markers.length} marker${markers.length === 1 ? '' : 's'}`}
        </Typography>
        {plotStatus !== PLOT_STATUSES.COMPLETE && (
          <IconButton
            size='small'
            onClick={onAddMarker}
            disabled={loading}
            color='primary'
          >
            <AddOutlinedIcon />
          </IconButton>
        )}
      </Stack>

      {loading ? (
        <Typography
          variant='body2'
          color='text.secondary'
          sx={{ fontSize: '0.75rem' }}
        >
          Loading...
        </Typography>
      ) : markers.length > 0 ? (
        <Stack spacing={0.5}>
          {markers.map((marker) => (
            <PlotMarker
              key={marker.entity_id}
              marker={marker}
              plotStatus={plotStatus}
              onDelete={handleDeleteMarker}
              plotId={plotId}
              waypointId={waypointId}
            />
          ))}
        </Stack>
      ) : null}

      <ErrorSnackBar message={api_error} onClear={() => setApiError(null)} />
    </Box>
  );
}

export default PlotMarkerList;

